import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usd, ago } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/shell/PageHeader";
import { Panel, ProvenanceTag, VerdictBadge, StatusBadge, TableSkeleton, EmptyState } from "@/components/common/primitives";
import { Wallet, Building2, History as HistoryIcon, Download, FileText } from "lucide-react";
import { toast } from "sonner";

export function Purchases() {
  return (
    <div className="space-y-4">
      <PageHeader title="Purchases & COGS" description="Cost layers created from approved receipts feed FIFO / weighted-average COGS." meta={<ProvenanceTag kind="fixture" />} />
      <Panel className="p-0 overflow-hidden">
        <table className="w-full text-compact">
          <thead><tr className="text-micro font-mono uppercase text-ink-muted text-left bg-surface-2">
            <th className="py-2 px-3">PO / Receipt</th><th className="px-3">Vendor</th><th className="px-3">Date</th><th className="px-3 text-right">Units</th><th className="px-3 text-right">Unit cost</th><th className="px-3 text-right">Landed total</th><th className="px-3">COGS policy</th>
          </tr></thead>
          <tbody>
            {[["SC-88213", "Sam's Club", "2026-02-03", 30, 7.61, 228.30, "Weighted avg"],
              ["CW-40122", "Costco", "2026-02-01", 12, 9.20, 110.40, "FIFO"],
              ["TG-71190", "Target", "2026-01-29", 6, 6.10, 36.60, "Weighted avg"]].map((r, i) => (
              <tr key={i} className="border-b border-edge-subtle last:border-0 hover:bg-surface-2">
                <td className="py-2 px-3 font-mono flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-ink-muted" />{r[0]}</td>
                <td className="px-3">{r[1]}</td><td className="px-3 font-mono">{r[2]}</td><td className="px-3 text-right font-mono">{r[3]}</td>
                <td className="px-3 text-right font-mono">{usd(r[4])}</td><td className="px-3 text-right font-mono">{usd(r[5])}</td>
                <td className="px-3 text-micro font-mono">{r[6]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

export function Sources() {
  const SUPPLIERS = [
    { name: "Sam's Club", products: 142, type: "wholesale", avg_roi: 44.8, sync: "2026-02-03T09:00:00Z" },
    { name: "Costco", products: 88, type: "wholesale", avg_roi: 31.2, sync: "2026-02-02T16:20:00Z" },
    { name: "Target", products: 51, type: "retail", avg_roi: 27.5, sync: "2026-02-01T11:05:00Z" },
    { name: "BJ's", products: 34, type: "wholesale", avg_roi: 22.1, sync: "2026-01-30T14:40:00Z" },
    { name: "Dollar General", products: 29, type: "retail", avg_roi: 36.4, sync: "2026-02-03T08:10:00Z" },
    { name: "Walmart Clearance", products: 47, type: "clearance", avg_roi: 39.9, sync: "2026-02-03T07:50:00Z" },
  ];
  const TYPE_TONE = { wholesale: "info", retail: "neutral", clearance: "success" };
  const totalProducts = SUPPLIERS.reduce((s, x) => s + x.products, 0);
  const blendedRoi = SUPPLIERS.reduce((s, x) => s + x.avg_roi * x.products, 0) / totalProducts;
  const th = "px-3 py-2 text-micro font-mono uppercase text-ink-muted font-medium whitespace-nowrap";
  return (
    <div className="space-y-4" data-testid="sources-page">
      <PageHeader title="Sources & Suppliers" description="Supplier catalog, vendor mappings and source products feeding your pipeline." meta={<ProvenanceTag kind="fixture" />} />
      <Panel className="px-1 py-1" testid="sources-ribbon">
        <div className="flex flex-wrap items-stretch divide-x divide-edge-subtle">
          {[["Suppliers", String(SUPPLIERS.length), null], ["Source products", String(totalProducts), null], ["Blended avg ROI", `${blendedRoi.toFixed(1)}%`, "text-emerald-600"]].map(([label, val, clr]) => (
            <div key={label} className="px-4 py-2 min-w-[130px]">
              <div className="text-micro uppercase tracking-wide text-ink-muted font-mono">{label}</div>
              <div className={cn("text-metricsm font-mono tabnum mt-0.5", clr || "text-ink-primary")}>{val}</div>
            </div>
          ))}
        </div>
      </Panel>
      <Panel className="p-0 overflow-hidden" testid="sources-surface">
        <div className="overflow-x-auto ez-scroll"><table className="w-full text-compact">
          <thead>
            <tr className="bg-surface-2 text-left border-b border-edge-subtle">
              <th className={th}>Supplier</th><th className={th}>Type</th>
              <th className={cn(th, "text-right")}>Source products</th><th className={cn(th, "text-right")}>Avg ROI</th><th className={cn(th, "text-right")}>Last sync</th>
            </tr>
          </thead>
          <tbody>
            {SUPPLIERS.map((s) => (
              <tr key={s.name} data-testid={`source-row-${s.name}`} className="border-b border-edge-subtle last:border-0 hover:bg-surface-1 h-12">
                <td className="px-3 text-ink-primary font-medium"><span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-ink-muted" />{s.name}</span></td>
                <td className="px-3"><StatusBadge tone={TYPE_TONE[s.type]} className="capitalize">{s.type}</StatusBadge></td>
                <td className="px-3 text-right font-mono tabnum">{s.products}</td>
                <td className={cn("px-3 text-right font-mono tabnum", s.avg_roi >= 30 ? "text-emerald-600" : "text-amber-600")}>{s.avg_roi.toFixed(1)}%</td>
                <td className="px-3 text-right text-micro text-ink-muted">{ago(s.sync)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Panel>
    </div>
  );
}

export function ScanHistory() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.scanHistory().then((r) => setRows(r.data)); }, []);
  if (!rows) return <TableSkeleton rows={4} />;
  return (
    <div className="space-y-4">
      <PageHeader title="Scan History" description="Every lookup with its verdict and provenance." meta={<ProvenanceTag kind="fixture" />} />
      <Panel className="p-0 overflow-hidden">
        <table className="w-full text-compact">
          <thead><tr className="text-micro font-mono uppercase text-ink-muted text-left bg-surface-2">
            <th className="py-2 px-3">Query</th><th className="px-3">Type</th><th className="px-3">Result</th><th className="px-3">Verdict</th><th className="px-3">When</th>
          </tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-edge-subtle last:border-0 hover:bg-surface-2">
                <td className="py-2 px-3 font-mono flex items-center gap-2"><HistoryIcon className="h-3.5 w-3.5 text-ink-muted" />{s.query}</td>
                <td className="px-3 text-micro font-mono">{s.type}</td><td className="px-3 text-ink-secondary">{s.result}</td>
                <td className="px-3"><VerdictBadge verdict={s.verdict} /></td><td className="px-3 text-micro text-ink-muted">{ago(s.at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

export function Exports() {
  return (
    <div className="space-y-4">
      <PageHeader title="Exports" description="Export qualifying leads, catalog results and structured receipt data." meta={<ProvenanceTag kind="fixture" />} />
      <div className="grid md:grid-cols-3 gap-3">
        {["Saved leads (CSV)", "Catalog qualifying rows (XLSX)", "Receipts → QuickBooks (CSV)"].map((e) => (
          <Panel key={e} className="p-4 flex items-center justify-between">
            <span className="text-compact text-ink-primary">{e}</span>
            <button onClick={() => toast.success("Export queued (fixture)")} className="h-8 px-2.5 rounded-ctrl border border-edge-subtle text-micro flex items-center gap-1"><Download className="h-3.5 w-3.5" />Export</button>
          </Panel>
        ))}
      </div>
    </div>
  );
}
